import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "app/store";
import { getEvaluationFormAiScoringQuestionsMaxOuterLimit } from "util/outerLimitUtils";

export type OuterLimitLoadStatus = "idle" | "loading" | "succeeded" | "failed";

type OuterLimitState = {
    evaluationFormAiScoringQuestionsMax: number | undefined;
    status: OuterLimitLoadStatus;
};

const initialState: OuterLimitState = {
    evaluationFormAiScoringQuestionsMax: undefined,
    status: "idle",
};

/**
 * Loads organization outer limit for AI scoring questions max (quality namespace).
 * Dispatched once when entering the evaluation form page — not tied to form field updates.
 */
export const loadMaxAiScoringQuestionsOuterLimit = createAsyncThunk(
    "outerLimit/loadMaxAiScoringQuestionsOuterLimit",
    async () => {
        return await getEvaluationFormAiScoringQuestionsMaxOuterLimit();
    },
    {
        condition: (_, { getState }): boolean => {
            const { status } = (getState() as RootState).outerLimit;
            return status !== "loading" && status !== "succeeded";
        },
    }
);

const outerLimitSlice = createSlice({
    name: "outerLimit",
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(loadMaxAiScoringQuestionsOuterLimit.pending, (state) => {
                state.status = "loading";
            })
            .addCase(
                loadMaxAiScoringQuestionsOuterLimit.fulfilled,
                (state, { payload }) => {
                    state.status = "succeeded";
                    state.evaluationFormAiScoringQuestionsMax = payload;
                }
            )
            .addCase(loadMaxAiScoringQuestionsOuterLimit.rejected, (state) => {
                state.status = "failed";
                state.evaluationFormAiScoringQuestionsMax = undefined;
            });
    },
});

export const selectEvaluationFormAiScoringQuestionsMaxOuterLimit = (
    state: RootState
) => state.outerLimit.evaluationFormAiScoringQuestionsMax;

export const selectOuterLimitLoadStatus = (state: RootState) =>
    state.outerLimit.status;

export default outerLimitSlice.reducer;
